import { formaterMontant, paiements as sch } from '@admin-pmvb/core'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import {
  api,
  corps,
  ErreurApi,
  ecrit,
  lire,
  type Paiement,
  requeteMoi,
  requetes,
  type VuePaiements,
} from '../api'
import { type Champ, Formulaire } from '../composants/Formulaire'
import { LogoConnu } from '../composants/Logo'
import { Icone } from '../composants/Icone'
import { EntetePage, Onglets } from '../composants/Ui'
import {
  jourCourt,
  LIBELLES_MOYEN,
  LIBELLES_RETARD,
  LIBELLES_STATUT_PAIEMENT,
  options,
  signe,
} from '../libelles'

const VUES: { vue: VuePaiements; libelle: string; aide: string }[] = [
  { vue: 'a_payer', libelle: 'À payer', aide: 'Paiements attendus, pas encore payés.' },
  { vue: 'a_encaisser', libelle: 'À encaisser', aide: 'Encaissements attendus.' },
  {
    vue: 'en_retard',
    libelle: 'En retard',
    aide: 'Date et délai de grâce dépassés, relevé à jour : le paiement manque vraiment.',
  },
  {
    vue: 'partiels',
    libelle: 'Partiels',
    aide: 'Une partie seulement est arrivée : le reste dû reste ouvert.',
  },
  {
    vue: 'a_rapprocher',
    libelle: 'À rapprocher',
    aide: 'Payés (cochés, ou réglés d’après leur pièce : reçu, carte), en attente du relevé.',
  },
  {
    vue: 'releves_manquants',
    libelle: 'Relevés manquants',
    aide: 'Délai dépassé mais le relevé ne couvre pas la période : importer un relevé plus récent.',
  },
]

export function BadgeRetard({ retard }: { retard: Paiement['retard'] }) {
  if (retard !== 'en_retard' && retard !== 'releve_manquant') return null
  return (
    <span className={`badge ${retard === 'en_retard' ? 'ton-alerte' : 'ton-ambre'}`}>
      {LIBELLES_RETARD[retard]}
    </span>
  )
}

export function LignePaiement({
  p,
  choisi,
  onChoisir,
  onPayer,
}: {
  p: Paiement
  choisi?: boolean
  onChoisir?: ((v: boolean) => void) | undefined
  onPayer?: (() => void) | undefined
}) {
  const reste = p.resteCts < p.montantDuCts
  const entree = p.sens === 'encaisser'
  return (
    <div className={`ligne ligne-paiement ${onChoisir ? '' : 'sans-choix'}`}>
      {onChoisir && (
        <span className="case">
          <input
            type="checkbox"
            aria-label="Choisir"
            checked={choisi ?? false}
            onChange={(e) => onChoisir(e.target.checked)}
          />
        </span>
      )}
      <span className={`pastille-sens ${entree ? 'entree' : ''}`}>
        <Icone nom={entree ? 'south_west' : 'north_east'} />
      </span>
      <div className="ligne-contenu">
        <div className="ligne-titre">
          <span className="titre-logo">
            <LogoConnu nom={p.tiers ?? p.libelle} taille={24} />
            <strong>{p.libelle ?? p.echeance ?? 'Paiement'}</strong>
          </span>
          <BadgeRetard retard={p.retard} />
        </div>
        <div className="discret">
          {[
            p.entite,
            p.tiers,
            p.datePrevue ? jourCourt(p.datePrevue) : null,
            p.moyen ? LIBELLES_MOYEN[p.moyen] : null,
            LIBELLES_STATUT_PAIEMENT[p.statut],
          ]
            .filter(Boolean)
            .join(' · ')}
          {p.echeanceId && (
            <>
              {' · '}
              <Link to="/echeances/$id" params={{ id: p.echeanceId }}>
                échéance
              </Link>
            </>
          )}
          {p.pieceId && (
            <>
              {' · '}
              <Link to="/pieces/$id" params={{ id: p.pieceId }}>
                pièce
              </Link>
            </>
          )}
        </div>
      </div>
      <div className="montant">
        <span className={entree ? 'entree' : ''}>
          {signe(p.sens)}
          {formaterMontant(reste ? p.resteCts : p.montantDuCts, p.devise)}
        </span>
        {reste && <span className="discret">sur {formaterMontant(p.montantDuCts, p.devise)}</span>}
      </div>
      {onPayer ? (
        <button type="button" className="bouton-discret petit" onClick={onPayer}>
          Payé
        </button>
      ) : (
        <span />
      )}
    </div>
  )
}

export function Paiements({
  vue,
  onChangeVue,
}: {
  vue: VuePaiements
  onChangeVue: (vue: VuePaiements) => void
}) {
  const client = useQueryClient()
  const moi = useQuery(requeteMoi).data
  const entites = useQuery(requetes.entites)
  const tiers = useQuery(requetes.tiers)
  const liste = useQuery(requetes.paiements({ vue }))
  const [choisis, setChoisis] = useState<Set<string>>(new Set())
  const [erreur, setErreur] = useState<string | null>(null)
  const [ajout, setAjout] = useState(false)
  const comptes = useQueries({
    queries: VUES.map((v) => requetes.paiements({ vue: v.vue })),
  })
  const ecrivables = (entites.data ?? []).filter((e) => !e.archivee)
  const aide = VUES.find((v) => v.vue === vue)!.aide
  const payable = (p: Paiement) => ['prevu', 'programme', 'partiel'].includes(p.statut)

  async function marquer(ids: string[]) {
    setErreur(null)
    try {
      await lire(api.paiements['marquer-payes'].$post({ json: { paiementIds: ids } }))
      setChoisis(new Set())
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.')
    }
  }

  const champs: Champ[] = [
    {
      nom: 'sens',
      libelle: 'Sens',
      type: 'choix',
      requis: true,
      options: [
        { valeur: 'payer', libelle: 'À payer' },
        { valeur: 'encaisser', libelle: 'À encaisser' },
      ],
    },
    {
      nom: 'entiteId',
      libelle: 'Entité',
      type: 'choix',
      requis: true,
      options: ecrivables.map((e) => ({ valeur: e.id, libelle: e.nom })),
    },
    {
      nom: 'libelle',
      libelle: 'Libellé',
      type: 'texte',
      aide: 'Ses mots aident le rapprochement (« Artisan plombier »).',
    },
    {
      nom: 'tiersId',
      libelle: 'Tiers',
      type: 'choix',
      options: (tiers.data ?? []).map((t) => ({ valeur: t.id, libelle: t.nom })),
    },
    { nom: 'montantDuCts', libelle: 'Montant (€)', type: 'montant', requis: true },
    { nom: 'datePrevue', libelle: 'Date prévue', type: 'date' },
    { nom: 'moyen', libelle: 'Moyen', type: 'choix', options: options(LIBELLES_MOYEN) },
  ]

  const choisisListe = (liste.data ?? []).filter((p) => choisis.has(p.id))
  const totalChoisis = choisisListe.reduce(
    (t, p) => t + (p.sens === 'payer' ? -1 : 1) * p.resteCts,
    0,
  )

  return (
    <main>
      <EntetePage
        surtitre="Trésorerie"
        titre="Paiements"
        action={
          ecrivables.length > 0
            ? { icone: 'add', libelle: 'Nouveau paiement', onClick: () => setAjout(true) }
            : undefined
        }
      />
      <div className="pile" style={{ gap: 16 }}>
        <Onglets
          valeur={vue}
          onChange={(v) => {
            onChangeVue(v)
            setChoisis(new Set())
          }}
          onglets={VUES.map((v, i) => ({
            valeur: v.vue,
            libelle: v.libelle,
            compte: comptes[i]?.data?.length,
          }))}
        />
        <p className="discret">{aide}</p>
        {ajout && (
          <section className="carte">
            <h2>Nouveau paiement</h2>
            <Formulaire
              champs={champs}
              initial={{ sens: 'payer' }}
              onAnnuler={() => setAjout(false)}
              onValider={async (v) => {
                await lire(api.paiements.$post({ json: corps(sch.paiementCreation, v) }))
                await client.invalidateQueries()
                setAjout(false)
              }}
            />
          </section>
        )}
        {erreur && <p className="alerte">{erreur}</p>}
        <section className="carte carte-flush">
          {choisis.size > 0 && (
            <div className="barre-selection">
              <Icone nom="check_box" taille={19} />
              <strong>{choisis.size} sélectionnés</strong>
              <span className="mono">
                {totalChoisis < 0 ? '\u2212' : '+'}
                {formaterMontant(Math.abs(totalChoisis))}
              </span>
              <button type="button" className="bouton-texte" onClick={() => setChoisis(new Set())}>
                Désélectionner
              </button>
              <button type="button" className="bouton" onClick={() => marquer([...choisis])}>
                <Icone nom="done_all" taille={17} />
                Marquer payés
              </button>
            </div>
          )}
          {liste.isPending && <p className="discret vide">Chargement…</p>}
          {liste.data?.length === 0 && <p className="discret vide">Rien ici.</p>}
          {liste.data?.map((p) => {
            const peutPayer =
              payable(p) && moi && ecrivables.some((e) => e.id === p.entiteId && ecrit(moi, e.id))
            return (
              <LignePaiement
                key={p.id}
                p={p}
                choisi={choisis.has(p.id)}
                onChoisir={
                  peutPayer
                    ? (v) =>
                        setChoisis((s) => {
                          const n = new Set(s)
                          if (v) n.add(p.id)
                          else n.delete(p.id)
                          return n
                        })
                    : undefined
                }
                onPayer={peutPayer ? () => marquer([p.id]) : undefined}
              />
            )
          })}
        </section>
      </div>
    </main>
  )
}
